import type { components } from '@multaqa/api-client'
import { type QueryClient, queryOptions, useQuery } from '@tanstack/react-query'
import { api } from './api'

/**
 * The full `/me` payload — user, group, every membership, and the effective
 * grants in the active company.
 *
 * See `apps/api/AGENTS.md` for tenancy: this is the one place the SPAs read
 * which company the user is in, which companies they could be in, and what
 * they are allowed to do in the one they currently are.
 */
type GeneratedMe = components['schemas']['MeResource']
type GeneratedMeUser = GeneratedMe['user']

/** See CurrentUser: old cached/fixture payloads may predate display preferences, avatars and the login phone. */
type MeUser = Omit<
  GeneratedMeUser,
  | 'avatar_url'
  | 'avatar_url_large'
  | 'pending_email'
  | 'login_phone'
  | 'timezone'
  | 'time_format'
  | 'shift_reminder_minutes'
> &
  Partial<
    Pick<GeneratedMeUser, 'avatar_url' | 'avatar_url_large' | 'pending_email' | 'login_phone'>
  > & {
    timezone?: string | null
    time_format?: string
    shift_reminder_minutes?: number
  }

export type Me = Omit<GeneratedMe, 'effective_grants_by_company' | 'user' | 'support_session'> & {
  user: MeUser
  /** Per-membership grants added by the group reporting API. */
  effective_grants_by_company?: Array<{
    company_id: string
    grants: Array<{ key: string; scope: string }>
  }>
  /** ADR-0033: set only for a support user. Absent or null means a customer. */
  support_session?: GeneratedMe['support_session']
}
export type Membership = Me['memberships'][number]
export type LocalizedString = components['schemas']['LocalizedString']

/** The permission which grants access to the group reporting workspace. */
export const GROUP_REPORT_PERMISSION = 'accounting_report_consolidated.view'

/**
 * `retry: false` for the same reason as `currentUserQuery` — a 401 is a
 * definitive answer, not a transient failure. Retrying it would delay the
 * redirect to the login screen by the length of the backoff.
 */
export const meQuery = queryOptions({
  queryKey: ['me'],
  retry: false,
  staleTime: 30_000,
  queryFn: async (): Promise<Me> => {
    const { data } = await api.GET('/me')
    if (!data) {
      throw new Error('GET /me returned no body')
    }
    return data.data
  },
})

/**
 * Make sure the session has an active company, choosing one if it does not.
 *
 * The backend deliberately never guesses: `/me` answers
 * `active_company_id: null` until something names a company, and MeApiTest
 * pins that — "no fallback, no guess". The consequence is that a freshly
 * signed-in session has no company scope at all, so `effective_grants` comes
 * back empty and the navigation rail — which is the permission set — renders
 * with nothing in it but Dashboard. The app looked broken on first login.
 *
 * So the CLIENT makes the choice, explicitly, through the same endpoint the
 * switcher uses. `POST /me/company` still validates the membership server-side,
 * so this grants no reach the user did not already have; it only saves them
 * from having to discover the switcher to make the app work at all.
 *
 * Called from `_authenticated`'s `beforeLoad`, which already awaits `/me` —
 * resolving it there rather than in an effect means the shell's first render
 * already has grants, so the rail never flashes empty.
 *
 * Returns the `Me` the caller should use. A user with no selectable membership
 * keeps `active_company_id: null` and sees "no scope", which is the honest
 * answer rather than a retry loop.
 */
export async function ensureActiveCompany(queryClient: QueryClient, me: Me): Promise<Me> {
  if (me.active_company_id !== null) return me

  const target = me.memberships.find((membership) => membership.status === 'active')
  if (!target) return me

  await api.POST('/me/company', { body: { company_id: target.company_id } })

  // The session now names a company, so every cached answer computed without
  // one is stale — `/me`'s own grants most of all.
  await queryClient.invalidateQueries()

  // `fetchQuery`, never `ensureQueryData`. `ensureQueryData` answers from the
  // cache whenever the cache holds data, and `beforeLoad` awaited `/me` one
  // step before calling this — so the cache holds the pre-company payload.
  // `invalidateQueries` does not repair that: it MARKS the query stale and
  // refetches active observers only, and a route guard mounts none. The
  // caller therefore read `active_company_id: null` and an empty grant list
  // every time. `fetchQuery` re-runs the query, because an invalidated query
  // is stale by definition.
  //
  // The navigation rail hid this for months: it reads `/me` through
  // `useQuery`, which mounts an observer, and an observer does refetch a
  // stale query. Only a caller inside `beforeLoad` saw the old payload.
  return queryClient.fetchQuery(meQuery)
}

/**
 * The active company's effective grants as a `can(key)` predicate.
 *
 * Presentation only — the API is the sole enforcer of permissions. Every
 * company-scoped endpoint authorises the request server-side, so a stale
 * grant still 403s; this hook only decides what the UI shows. While `/me`
 * is loading, errored, or when the membership holds no grants, `can`
 * answers false, which is the safe direction for a navigation rail.
 */
export function useEffectiveGrants(): { can: (permissionKey: string) => boolean } {
  const { data: me } = useQuery(meQuery)
  const granted = new Set<string>((me?.effective_grants ?? []).map((grant) => grant.key))
  return {
    can: (permissionKey: string) => granted.has(permissionKey),
  }
}

/**
 * Derive permission reach from one cached `/me` payload. A grant is usable for
 * group reporting only when both the membership and its company are active.
 * This is presentation data only; every report endpoint still authorises and
 * scopes on the server.
 *
 * Missing, loading, and failed `/me` responses deliberately return no reach.
 */
export function companyReachFromMe(me: Me | undefined, permissionKey: string): string[] {
  const grantsByCompany = new Map(
    (me?.effective_grants_by_company ?? []).map((company) => [company.company_id, company.grants]),
  )
  const reachable = new Set<string>()

  for (const membership of me?.memberships ?? []) {
    if (membership.status !== 'active' || membership.company_status !== 'active') continue

    const grants = grantsByCompany.get(membership.company_id) ?? []
    if (grants.some((grant) => grant.key === permissionKey)) {
      reachable.add(membership.company_id)
    }
  }

  return [...reachable]
}

/** Read permission reach from the already-cached `/me`; this helper never fetches. */
export function getCompanyReach(queryClient: QueryClient, permissionKey: string): string[] {
  const state = queryClient.getQueryState<Me>(meQuery.queryKey)
  if (state?.status !== 'success') return []
  return companyReachFromMe(state.data, permissionKey)
}

export function useCompanyReach(permissionKey: string): string[] {
  const query = useQuery(meQuery)
  if (query.isPending || query.isError) return []
  return companyReachFromMe(query.data, permissionKey)
}

/**
 * Does the resolved `/me` payload grant `permissionKey` in the active company?
 *
 * The query-client sibling of `useEffectiveGrants`, for route `beforeLoad`
 * guards, which run before the component tree renders so there is no hook to
 * call. Same Set-of-keys semantics and the same safe direction — a payload
 * that is absent or errored grants nothing. `_authenticated`'s `beforeLoad`
 * has already ensured `/me` is cached (parents run first), so this reads, it
 * never fetches. Keep the two implementations in step.
 */
export function hasGrant(queryClient: QueryClient, permissionKey: string): boolean {
  const me = queryClient.getQueryData<Me>(meQuery.queryKey)
  return me?.effective_grants.some((grant) => grant.key === permissionKey) ?? false
}

/**
 * Pick the localized text in the active language, falling back to English and
 * then to the first non-empty value. The backend stores text as `{ en, ar }`
 * (`Localizable` cast) so a row never blocks a screen on a missing locale, and
 * a translator leaving a key blank is visible here, not on the user's screen.
 */
export function localizedText(
  value: Record<string, unknown> | null | undefined,
  locale: string,
): string {
  if (!value) return ''
  const base = locale.split('-')[0] ?? 'en'
  const fromLocale = value[base]
  if (typeof fromLocale === 'string' && fromLocale.length > 0) return fromLocale
  const fromEnglish = value.en
  if (typeof fromEnglish === 'string' && fromEnglish.length > 0) return fromEnglish
  for (const candidate of Object.values(value)) {
    if (typeof candidate === 'string' && candidate.length > 0) return candidate
  }
  return ''
}

/**
 * Pick the employee name in the reader's script.
 *
 * The employee list resource sends `display_name` and `display_name_ar` as two
 * columns rather than one localized object, so `localizedText` cannot read it.
 * Every picker and register that shows a person goes through here, because a
 * Latin name inside an Arabic screen is the defect this exists to stop. A
 * missing Arabic name falls back to the Latin one — a blank cell is worse.
 */
export function employeeName(
  displayName: string | null | undefined,
  displayNameAr: string | null | undefined,
  locale: string,
): string {
  const preferred = locale.startsWith('ar') ? displayNameAr : displayName
  return preferred || displayName || displayNameAr || ''
}

/**
 * The word the active company's seal writes (`KuficSeal` `name`): the Arabic
 * trading name in every language, as the top bar draws it. The English name
 * holds no Arabic word, so a seal given it fell back to the code's geometric
 * mark and looked like another company's logo (b593).
 */
export function useCompanySealName(): string {
  const { data: me } = useQuery(meQuery)
  const active = (me?.memberships ?? []).find(
    (membership) => membership.company_id === me?.active_company_id,
  )
  return localizedText(active?.company_trading_name, 'ar')
}
